import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { LinearGradient } from 'expo-linear-gradient';
import { Sparkles } from 'lucide-react-native';
import React, { memo, useEffect } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  useAIAssistantActions,
  useAIAssistantButtonVisible,
  useAIAssistantState,
  useAIAssistantVisible,
} from './AIAssistantProvider';

const GRADIENT_COLORS = ['#6366F1', '#3B82F6', '#06B6D4'] as const;
const GRADIENT_STREAMING = ['#8B5CF6', '#6366F1', '#3B82F6'] as const;

export const AIAssistantButton = memo(function AIAssistantButton() {
  const { rpx } = useResponsiveContext();
  const insets = useSafeAreaInsets();
  const actions = useAIAssistantActions();
  const isVisible = useAIAssistantVisible();
  const state = useAIAssistantState();
  const buttonVisible = useAIAssistantButtonVisible();

  const scale = useSharedValue(1);
  const shadowOpacity = useSharedValue(0.4);
  const glowRadius = useSharedValue(16);

  useEffect(() => {
    if (state.isStreaming) {
      scale.value = withRepeat(
        withSequence(
          withTiming(1.1, {
            duration: 800,
            easing: Easing.inOut(Easing.ease),
          }),
          withTiming(1, {
            duration: 800,
            easing: Easing.inOut(Easing.ease),
          }),
        ),
        -1,
        true,
      );
      shadowOpacity.value = withRepeat(
        withSequence(
          withTiming(0.6, { duration: 800 }),
          withTiming(0.35, { duration: 800 }),
        ),
        -1,
        true,
      );
      glowRadius.value = withRepeat(
        withSequence(
          withTiming(24, { duration: 800 }),
          withTiming(16, { duration: 800 }),
        ),
        -1,
        true,
      );
    } else {
      scale.value = withTiming(1, { duration: 300 });
      shadowOpacity.value = withTiming(0.4, { duration: 300 });
      glowRadius.value = withTiming(16, { duration: 300 });
    }
  }, [state.isStreaming, scale, shadowOpacity, glowRadius]);

  const animatedScale = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
  }));

  const animatedGlow = useAnimatedStyle(() => ({
    shadowOpacity: shadowOpacity.value,
    shadowRadius: glowRadius.value,
  }));

  const isMobile = useIsMobileLayout();
  // Hidden unless a focused screen has opted in via
  // `useAIAssistantForPage`. The FAB knows nothing about which screen.
  if (!buttonVisible) return null;
  if (isVisible && isMobile) return null;

  const buttonSize = rpx(52);
  const colors = state.isStreaming ? GRADIENT_STREAMING : GRADIENT_COLORS;

  return (
    <Animated.View
      style={[
        styles.container,
        { right: rpx(16), bottom: insets.bottom + rpx(80) },
        animatedScale,
      ]}
    >
      <Animated.View
        style={[
          styles.outerShadow,
          {
            width: buttonSize,
            height: buttonSize,
            borderRadius: buttonSize / 2,
          },
          animatedGlow,
        ]}
      >
        <TouchableOpacity activeOpacity={0.85} onPress={actions.toggle}>
          <LinearGradient
            colors={colors as unknown as string[]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={[
              styles.gradient,
              {
                width: buttonSize,
                height: buttonSize,
                borderRadius: buttonSize / 2,
              },
            ]}
          >
            <Sparkles color="#fff" size={rpx(22)} strokeWidth={2.2} />
          </LinearGradient>
          {state.isStreaming && (
            <View
              style={[
                styles.streamingDot,
                {
                  right: rpx(0),
                  top: rpx(0),
                  width: rpx(12),
                  height: rpx(12),
                  borderRadius: rpx(6),
                  borderWidth: rpx(2),
                },
              ]}
            />
          )}
        </TouchableOpacity>
      </Animated.View>
    </Animated.View>
  );
});

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    zIndex: 999,
  },
  outerShadow: {
    shadowColor: '#3730A3',
    shadowOffset: { width: 0, height: 8 },
    elevation: 24,
  },
  gradient: {
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  streamingDot: {
    position: 'absolute',
    backgroundColor: '#22C55E',
    borderColor: '#fff',
  },
});
